import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { Paper, Typography, Box, Chip, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PhoneInTalkIcon from "@material-ui/icons/PhoneInTalk";
import CallIcon from "@material-ui/icons/Call";
import CallEndIcon from "@material-ui/icons/CallEnd";
import { AuthContext } from "../../context/Auth/AuthContext";
import openSocket from "../../services/socket-io";
import { isMetaCallRejectConfigured, rejectMetaCall } from "../../services/metaCallReject";
import { pruneMetaCallAlerts, reduceMetaCallAlerts } from "../../services/metaCallAlerts";
import { isMetaCallAcceptMockConfigured, startMetaCallAcceptMock } from "../../services/metaCallAcceptMock";
import MetaCallStageControls from "../MetaCallStageControls";
import { isMetaCallStageControlConfigured } from "../../services/metaCallStageControl";

const useStyles = makeStyles(theme => ({
  container: { position: "fixed", top: theme.spacing(10), right: theme.spacing(2),
    width: "calc(100vw - 32px)", maxWidth: 360, zIndex: theme.zIndex.snackbar, pointerEvents: "none" },
  notice: { padding: theme.spacing(2), marginBottom: theme.spacing(1), borderLeft: "4px solid #228b5b",
    borderRadius: 10, pointerEvents: "auto", boxShadow: theme.shadows[8] },
  heading: { display: "flex", alignItems: "center", gap: theme.spacing(1), marginBottom: theme.spacing(1) },
  phoneIcon: { color: "#228b5b" },
  actions: { display: "flex", gap: theme.spacing(1), marginTop: theme.spacing(2), marginBottom: theme.spacing(1) },
  actionButton: { flex: 1 },
  caption: { marginTop: theme.spacing(1), display: "block" }
}));

const MetaCallAlerts = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, isAuth } = useContext(AuthContext);
  const id = Number(user && user.id);
  const identity = isAuth && Number.isSafeInteger(id) && id > 0
    ? String(id) + ":" + String(user.profile || "") : "";
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const jobs = useRef(new Map());
  const epoch = useRef(0);
  const attempts = useRef({ identity, callIds: new Set() });
  if (attempts.current.identity !== identity) attempts.current = { identity, callIds: new Set() };
  const [session, setSession] = useState({ identity: "", alerts: [] });
  const [decisions, setDecisions] = useState({ identity: "", statuses: {} });
  const [stageSignal, setStageSignal] = useState(null);
  const [inlineTarget, setInlineTarget] = useState(null);
  const [stagePanelOpen, setStagePanelOpen] = useState(false);
  const alerts = session.identity === identity ? session.alerts : [];
  const alertsRef = useRef(alerts);
  alertsRef.current = alerts;

  useEffect(() => {
    setSession({ identity, alerts: [] });
    if (!identity) return undefined;
    let active = true;
    let socket;
    const stopAll = () => {
      jobs.current.forEach(job => job.cancel());
      jobs.current.clear();
    };
    try { socket = openSocket(); } catch (_) { return undefined; }
    const onMetaCall = data => {
      if (!active) return;
      setStageSignal({ identity, data });
      // Cancel synchronously; do not wait for the next React render.
      jobs.current.forEach((job, callId) => {
        if (data && ((data.action === "ended" && data.callId === callId) ||
            (data.action === "ringing" && data.ticketId === job.ticketId && data.callId !== callId))) job.cancel();
      });
      setSession(current => current.identity === identity ? {
        identity, alerts: reduceMetaCallAlerts(current.alerts, data, Date.now())
      } : current);
    };
    const onDisconnect = () => {
      if (!active) return;
      epoch.current += 1;
      stopAll();
      setSession(current => current.identity === identity ? { identity, alerts: [] } : current);
    };
    socket.on("metaCall", onMetaCall);
    socket.on("disconnect", onDisconnect);
    const interval = window.setInterval(() => {
      if (!active) return;
      jobs.current.forEach(job => { if (job.expiresAtMs <= Date.now()) job.cancel(); });
      setSession(current => {
        if (current.identity !== identity) return current;
        const next = pruneMetaCallAlerts(current.alerts, Date.now());
        return next.length === current.alerts.length ? current : { identity, alerts: next };
      });
    }, 1000);
    return () => {
      active = false;
      epoch.current += 1;
      stopAll();
      window.clearInterval(interval);
      socket.off("metaCall", onMetaCall);
      socket.off("disconnect", onDisconnect);
      socket.disconnect();
    };
  }, [identity]);

  useEffect(() => {
    jobs.current.forEach((job, callId) => {
      if (!alerts.some(call => call.callId === callId)) job.cancel();
    });
  }, [alerts]);

  // META_CALL_INLINE_ALERT_V1
  // Solo cambia la posicion del aviso; no duplica acciones.
  useEffect(() => {
    const updateTarget = () => {
      const target = document.getElementById(
        "manda-meta-call-inline-anchor"
      );

      setInlineTarget(identity ? target : null);
    };

    window.addEventListener(
      "manda-meta-call-inline-target-changed",
      updateTarget
    );

    updateTarget();

    return () => {
      window.removeEventListener(
        "manda-meta-call-inline-target-changed",
        updateTarget
      );
    };
  }, [identity]);
  const statusOf = callId => decisions.identity === identity ? decisions.statuses[callId] : undefined;
  const canAccept = Boolean(identity) && isMetaCallAcceptMockConfigured();
  const canReject = Boolean(identity) && isMetaCallRejectConfigured();
  const busy = alerts.some(call => ["accepting", "sending"].includes(statusOf(call.callId)));

  const handleDecision = async (call, action) => {
    if (!identity || identityRef.current !== identity ||
        !alerts.some(item => item.callId === call.callId) || call.expiresAtMs <= Date.now() ||
        (action === "accept" ? !isMetaCallAcceptMockConfigured() : !isMetaCallRejectConfigured())) return;
    const registry = attempts.current;
    if (registry.identity !== identity || registry.callIds.has(call.callId) || jobs.current.size) return;
    registry.callIds.add(call.callId);
    const actionEpoch = epoch.current;
    const isCurrent = () => identityRef.current === identity && epoch.current === actionEpoch &&
      alertsRef.current.some(item => item.callId === call.callId && item.expiresAtMs > Date.now());
    const setStatus = status => {
      if (identityRef.current !== identity || epoch.current !== actionEpoch) return;
      setDecisions(current => ({ identity, statuses: {
        ...(current.identity === identity ? current.statuses : {}), [call.callId]: status
      } }));
    };
    // Reserve before the first await, shared by both buttons.
    const entry = { cancel: () => {}, ticketId: call.ticketId, expiresAtMs: call.expiresAtMs };
    jobs.current.set(call.callId, entry);
    setStatus(action === "accept" ? "accepting" : "sending");
    try {
      if (action === "accept") {
        const task = startMetaCallAcceptMock({ callId: call.callId, expiresAtMs: call.expiresAtMs, isCurrent });
        entry.cancel = task.cancel;
        await task.promise;
        if (isCurrent()) setStatus("reserved_test");
      } else {
        const result = await rejectMetaCall(call.callId);
        if (isCurrent()) setStatus(result === "provider_confirmed" ? "provider_confirmed" : "uncertain");
      }
    } catch (_) {
      setStatus(action === "accept" ? "mock_unconfirmed" : "unconfirmed");
    } finally {
      if (jobs.current.get(call.callId) === entry) jobs.current.delete(call.callId);
    }
  };

  const captions = {
    accepting: "Preparando simulacion. Chrome puede solicitar acceso al microfono...",
    reserved_test: "Simulacion completada: reserva local. Audio sin conectar; microfono cerrado.",
    mock_unconfirmed: "Simulacion interrumpida o sin confirmar. No se reintentara automaticamente.",
    sending: "Solicitando rechazo. Espera...",
    provider_confirmed: "Meta confirmo la solicitud de rechazo.",
    uncertain: "Resultado incierto. No vuelvas a intentarlo.",
    unconfirmed: "No se pudo confirmar el resultado. No se repetira automaticamente."
  };
  if (!identity) return null;
  return (
    <div className={classes.container} role="status" aria-live="polite" aria-label="Avisos de llamadas entrantes">
      {alerts.map(call => {
        const target = inlineTarget &&
          inlineTarget.isConnected &&
          inlineTarget.dataset.metaCallId === String(call.callId) &&
          inlineTarget.dataset.ticketId === String(call.ticketId)
            ? inlineTarget
            : null;

        const notice = (
        <Paper key={call.callId} className={classes.notice} elevation={8}>
          <div className={classes.heading}>
            <PhoneInTalkIcon className={classes.phoneIcon} />
            <Typography variant="subtitle1">Llamada entrante</Typography>
          </div>
          <Box mb={1}><Typography variant="body2">Conversaci&oacute;n #{call.ticketId}</Typography></Box>
          {/* META_CALL_TICKET_LINK_V1 */}
          {["admin", "user"].includes(user?.profile) &&
            ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
            /^[1-9][0-9]{0,14}$/.test(String(call.ticketId)) && (
              <Box mb={1}>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => history.push(
                    `/tickets?metaCallTicket=${encodeURIComponent(String(call.ticketId))}&metaCallId=${encodeURIComponent(String(call.callId))}`
                  )}
                >
                  Ver conversacion
                </Button>
              </Box>
            )}
          <Chip label={canAccept ? "WhatsApp - simulacion" : "Llamada por WhatsApp"} size="small" variant="outlined" />
          <Box className={classes.actions}>
            <Button className={classes.actionButton} variant="outlined" startIcon={<CallEndIcon />}
              onClick={() => handleDecision(call, "reject")}
              disabled={!canReject || busy || Boolean(statusOf(call.callId))}>Rechazar</Button>
            <Button className={classes.actionButton} variant="contained" color="primary" startIcon={<CallIcon />}
              onClick={() => handleDecision(call, "accept")}
              disabled={!canAccept || busy || Boolean(statusOf(call.callId))}>Contestar</Button>
          </Box>
          {statusOf(call.callId) === "accepting" && (
            <Button size="small" onClick={() => jobs.current.get(call.callId)?.cancel()}>Cancelar simulacion</Button>
          )}
          <Typography variant="caption" color="textSecondary" className={classes.caption}>
            {captions[statusOf(call.callId)] || (canAccept
              ? "Contestar prueba el microfono y prepara SDP. No conecta una llamada real."
              : canReject ? "Rechazar requiere autorizacion vigente. Contestar simulado esta deshabilitado."
                : "Controles deshabilitados hasta contar con un backend de pruebas verificado.")}
          </Typography>
        </Paper>
        );

        return target
          ? createPortal(notice, target, call.callId)
          : notice;
      })}
      {/* META_CALL_FLOATING_COLLAPSE_V1 */}
      {isMetaCallStageControlConfigured() && (
        <>
          <Box
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 4
            }}
          >
            <Button
              size="small"
              variant="contained"
              color="primary"
              aria-expanded={stagePanelOpen}
              aria-controls="meta-stage-floating-panel"
              onClick={() => setStagePanelOpen(previous => !previous)}
              style={{
                pointerEvents: "auto",
                borderRadius: 20,
                textTransform: "none"
              }}
            >
              {stagePanelOpen
                ? "Ocultar controles de prueba"
                : "Controles de prueba"}
            </Button>
          </Box>

          <Box
            id="meta-stage-floating-panel"
            style={{
              display: stagePanelOpen ? "block" : "none"
            }}
            aria-hidden={!stagePanelOpen}
          >
            <MetaCallStageControls
              identity={identity}
              signal={stageSignal && stageSignal.identity === identity ? stageSignal.data : null}
            />
          </Box>
        </>
      )}
    </div>
  );
};
export default MetaCallAlerts;